/**
 * Whether somebody is signed in, as far as the *other* origin can tell.
 *
 * The storefront is a server-rendered Worker on `goodbuild.cc` and the session
 * is a bearer token in `localStorage` on `app.goodbuild.cc`. Neither half of
 * that can see the other: the store's frontmatter has no token to send to
 * `/auth/get-session`, and a script on the store cannot read another origin's
 * storage — which is the same wall that made the old buy button answer 401 on
 * every click. So the store's header rendered `signedIn={false}` for everyone,
 * and a player who had signed in an hour ago was still being offered `Sign in`
 * with no way to reach their own library from the catalogue.
 *
 * **This is a hint, and the distinction is the whole design.** The value is the
 * character `1` and, since #62, up to three letters after it naming which of
 * the role links the bar may draw. It names nobody, carries no signature and
 * grants nothing: the only thing it changes is which links the header draws.
 * Every one of those links leads to the React app, which asks the API for a
 * real session and refuses if there is not one.
 *
 * **Adding the roles did not change that property, and it must not.** `1cda`
 * is not a claim to be a creator, a developer or an admin — it is a claim that
 * the header should draw three more links, each of which leads somewhere that
 * checks. `/board`, `/portal` and `/admin` are routes in the React app behind
 * the same session gate as `/library`, and the API behind them authorises every
 * read on the bearer token rather than on anything a browser volunteered. So
 * the worst a forged `1cda` buys is three links that answer "sign in" — the
 * same three links a bookmark would have bought, one keystroke later. The
 * moment anything on either side reads a letter of this value and *believes*
 * it, that stops being true, and this whole file has to be reconsidered rather
 * than extended.
 *
 * That is what makes it a different proposition from the shared session cookie
 * `apps/app/src/routes/game.tsx` argues against, and the argument there is worth
 * reading before touching this file. The API sets `credentials: false` on its
 * CORS policy so that a cookie *cannot* carry authority, which is what makes
 * the surface immune to CSRF by construction. Forging this cookie buys an
 * attacker the word "Account" in their own header. Nothing reads it for
 * authorisation, and nothing may start to — the moment it decides anything, the
 * property that choice bought is gone.
 *
 * Being a hint also means it is allowed to be wrong. A session that expires,
 * or a sign-out in another browser, leaves it set until the app next loads; the
 * cost is a header offering `Library` to somebody who then gets sent to
 * sign-in. That is the same place `Sign in` would have sent them, one click
 * later.
 *
 * **Only a browser ever writes this, and that is the shape of every staleness
 * it has.** Nothing on the server can reach into a cookie on somebody else's
 * machine, so a role an administrator grants at four o'clock reaches the
 * storefront when that person next loads the React app and not a moment before
 * — until then their store header is a link short of what their account can
 * open. The React app is right immediately, because it asks `/me`; the store is
 * right on the next visit. Making it any better would mean the store asking the
 * API who the visitor is, which is the identity layer this whole mechanism
 * exists so that it does not need.
 *
 * **The name is not here, and #113 is where that was decided.** The storefront
 * needed to greet somebody by name the way the React app's bar does, and the
 * obvious place to put it was a fifth field in this value. That would have
 * ended every argument above in one line: a cookie that names a person is a
 * cookie that identifies one, and the reason nothing here needs a signature is
 * precisely that it names nobody. So the name went into its own cookie with its
 * own written contract — `display-name.ts` beside this file — and the two are
 * deliberately separable. This one stays four booleans. `session-hint.test.ts`
 * pins that as a shape rather than as a string, so adding a role does not fail
 * it and adding a name does.
 */

import { SITES } from './sites';

/**
 * The cookie's name.
 *
 * Deliberately not `session`, `token` or anything else that reads as a
 * credential in a browser inspector or a log. Whoever finds this next should be
 * able to tell from the name alone that it is not the session.
 */
export const SESSION_HINT_COOKIE = 'gb_signed_in';

/**
 * What the storefront is allowed to know about the visitor.
 *
 * Deliberately the same four booleans as `NavStanding` in `apps/app/src/ui`, and
 * deliberately not an import of it: `ui` depends on this package and not the
 * other way round, and a navigation type is the chrome's contract rather than
 * the cookie's. The two are held together structurally instead, at the one
 * place that bridged them — the storefront's layout handed this
 * straight to `SiteNav`, so if they ever diverge the store's `astro check`
 * fails rather than the store quietly drawing the wrong bar.
 *
 * Booleans rather than the `applied`/`active`/`suspended` states the account
 * page distinguishes, for the same reason `topNav` takes them: a bar has one
 * question per role, which is whether to draw the link. An applied creator
 * cannot open the board, so as far as this cookie is concerned they are not a
 * creator, and the account page is where they read why.
 *
 * **There were three roles and now there are two** (#144). `developer` drew a
 * `Developers` entry pointing at the studio portal, and `USER_ROLES` no longer
 * has the role. Its letter is retired below rather than reused.
 */
export interface SessionHint {
  signedIn: boolean;
  creator: boolean;
  admin: boolean;
}

/** Nobody, as far as a header can tell. What every request without one gets. */
export const NO_SESSION_HINT: SessionHint = Object.freeze({
  signedIn: false,
  creator: false,
  admin: false,
});

type SessionHintRole = Exclude<keyof SessionHint, 'signedIn'>;

/** The character that says somebody is signed in, and nothing more than that. */
const PRESENT = '1';

/**
 * The letter each role is written as, in the order they are written.
 *
 * **A cookie already in a browser holding the bare `1` has to keep reading as
 * "signed in, no roles",** which is what the encoding is shaped around: the
 * roles are a suffix, so every hint written before #62 goes on meaning exactly
 * what it meant. Nobody is signed out by a deploy, and nothing has to be
 * migrated — the app rewrites the value on the next load and the letters
 * appear then.
 *
 * One letter each rather than a JSON object or a bitfield. A bitfield reads as
 * something that was meant to be hard to read, which is the wrong thing for a
 * value we want a person to be able to look at in a browser inspector and
 * dismiss as harmless; JSON would have to be escaped in a cookie, and would
 * invite somebody to put a name in it.
 */
const ROLE_LETTERS: readonly (readonly [SessionHintRole, string])[] = [
  ['creator', 'c'],
  /*
    `['developer', 'd']` was here and went with the studios (#144).

    **`d` is retired rather than freed.** A cookie written before this deploy
    may still carry it, and `parseHint` ignores any letter it does not know — so
    an old hint reads as "signed in, creator and admin as written", which is
    correct. Giving `d` to a future role would make those same cookies claim it.
  */
  ['admin', 'a'],
];

/**
 * The value the cookie carries, from what the app knows.
 *
 * `true` rather than a standing means *signed in, roles unknown* — which is the
 * honest thing for the two callers that have no standing to give: a sign-out,
 * where there is nothing to describe, and any caller that has a session but has
 * not yet heard back from `/me`. It writes the bare `1`, and a bar drawn from
 * it is a link or two short rather than a link too many.
 */
function hintValue(hint: SessionHint | true): string {
  if (hint === true) return PRESENT;

  let value = PRESENT;
  for (const [role, letter] of ROLE_LETTERS) {
    if (hint[role]) value += letter;
  }
  return value;
}

/**
 * What a value means, or nobody.
 *
 * Strict where it has to be and forgiving where being strict would sign people
 * out. The leading `1` is required, so a cleared cookie a browser kept, a `0`,
 * a `true`, an `11` or anything another tool wrote under this name is not a
 * visitor — those are the cases the old `hasSessionHint` refused and they all
 * still get refused here.
 *
 * **A letter this version has never heard of is ignored rather than fatal**,
 * and that is the one place the strictness is deliberately relaxed. The store
 * and the app are two Workers deployed separately, so a browser carrying a hint
 * written by a newer app will hand it to an older store — and reading `1cx` as
 * *signed out* would sign a player out of their own header for the length of a
 * deploy. Reading it as "signed in, creator" is wrong only about a link that
 * does not exist yet. Anything that is not a lowercase letter is still fatal,
 * because that is a value we did not write.
 */
function parseHint(value: string): SessionHint {
  if (!value.startsWith(PRESENT)) return NO_SESSION_HINT;

  const letters = value.slice(PRESENT.length);
  if (!/^[a-z]*$/.test(letters)) return NO_SESSION_HINT;

  const hint: SessionHint = { signedIn: true, creator: false, admin: false };
  for (const [role, letter] of ROLE_LETTERS) {
    if (letters.includes(letter)) hint[role] = true;
  }
  return hint;
}

/**
 * How long the hint outlives the page that set it.
 *
 * Better Auth's session default is seven days and nothing here overrides it, so
 * the hint expires alongside the thing it describes rather than before or long
 * after. The app rewrites it on every load, so an active session keeps pushing
 * the window forward the same way the session itself does.
 *
 * **Exported since #113, and only so that the second cookie cannot drift from
 * this one.** `display-name.ts` writes `gb_name` in the same breath as this
 * value is written and clears it in the same breath as this value is cleared,
 * which is only true while both are given the same window; a lifetime copied
 * across as a literal would be two numbers that agree until somebody edits one,
 * and the visible failure would be a store header still greeting a person by
 * name days after it stopped believing they were signed in. Nothing else may
 * read this to mean anything about a session — the session's own lifetime is
 * Better Auth's, and this is a hint's.
 */
export const SESSION_HINT_LIFETIME_SECONDS = 60 * 60 * 24 * 7;

/** Just enough of `window.location` to decide the attributes. */
export interface HintLocation {
  hostname: string;
  protocol: string;
}

/**
 * The domain the hint is shared under, or null to keep it to this host.
 *
 * A cookie is scoped by registrable domain and ignores the port, and both
 * halves of that matter here:
 *
 * — In production the shop is `goodbuild.cc` and the same bundle also answers
 *   on `app.`, `creators.` and `developers.`, so `Domain=goodbuild.cc` reaches
 *   all four.
 * — Locally everything is one dev server on `localhost:5173`. A host-only
 *   cookie already covers it, and asking for a domain on `localhost` would be
 *   refused.
 *
 * **The hint is one origin's business now and it is kept anyway.** It exists
 * because the storefront was a separate Worker that had to draw a signed-in bar
 * without a session, and that Worker is gone — the bar is drawn from the
 * session directly. What it still buys is the four subdomains: a person signed
 * in on `goodbuild.cc` and opening `developers.goodbuild.cc` is a cross-origin
 * hop as far as `localStorage` is concerned, and this is what stops that
 * reading as signed out for the length of one page load.
 *
 * **The generated `*.workers.dev` dev deployments cannot share it**, and that is
 * a limit rather than an oversight: `workers.dev` is on the public suffix list,
 * so no browser will accept a cookie scoped to it. Those two Workers are
 * unrelated origins as far as a browser is concerned, the store there falls
 * back to `Sign in`, and that is the same thing it does today.
 */
export function sessionHintDomain(hostname: string): string | null {
  const host = hostname.toLowerCase();
  const site = SITES.store.host;
  return host === site || host.endsWith(`.${site}`) ? site : null;
}

/**
 * The `document.cookie` string that sets or clears the hint.
 *
 * Returned rather than assigned, so this stays a pure function that a test can
 * read — the app does the one assignment, in `apps/app/src/session-hint.ts`.
 *
 * `SameSite=Lax` rather than `Strict`. Most arrivals at the storefront are
 * somebody following a creator's link from Discord or X, which is a cross-site
 * top-level navigation: under `Strict` the hint would be withheld on exactly
 * that first page load and appear on the second, so the header would offer
 * `Sign in` to a signed-in player and then quietly change its mind. `Lax` is
 * sent on a top-level GET however the visitor got there, which is every request
 * that renders this header.
 *
 * Not `HttpOnly`: the app is static assets with no server of its own, so the
 * browser is the only thing that can write this.
 *
 * Takes a standing, or a bare boolean for the two callers that have none —
 * `false` to clear it on sign-out, `true` to say "signed in, roles unknown".
 * Keeping the boolean is not politeness to old call sites: it is the only way
 * to say *unknown*, and an all-false standing would say *none*, which is a
 * different and wrong thing to write over a correct hint.
 */
export function sessionHintCookie(hint: boolean | SessionHint, location: HintLocation): string {
  const domain = sessionHintDomain(location.hostname);

  /*
   * What there is to write, or nothing at all.
   *
   * `false` and a standing whose `signedIn` is false are the same instruction,
   * and collapsing them here rather than at the call sites is what keeps the
   * two ways of saying it from drifting: `Shell` clears the hint by passing the
   * standing it already has, and `endSession` clears it by passing `false`,
   * because at that point there is no standing left to pass.
   */
  let carried: SessionHint | true | null = null;
  if (hint === true) carried = true;
  else if (hint !== false && hint.signedIn) carried = hint;

  const attributes = [
    `${SESSION_HINT_COOKIE}=${carried ? hintValue(carried) : ''}`,
    'Path=/',
    'SameSite=Lax',
    // Clearing is expiring it in the past rather than writing an empty value,
    // which a browser would otherwise keep and `readSessionHint` would then
    // have to know to disbelieve.
    `Max-Age=${carried ? SESSION_HINT_LIFETIME_SECONDS : 0}`,
  ];

  if (domain) attributes.push(`Domain=${domain}`);
  // A `Secure` cookie is dropped over plain http, which is what a local dev
  // server serves. Keyed off the protocol rather than the hostname because
  // browsers disagree about whether localhost counts as a secure context.
  if (location.protocol === 'https:') attributes.push('Secure');

  return attributes.join('; ');
}

/**
 * What a request says about itself.
 *
 * Takes the raw `Cookie` header, because the one caller is Astro middleware and
 * that is what it has. Answers `NO_SESSION_HINT` for a request that carries
 * none — which is every crawler and most of the traffic the storefront exists
 * to serve, so it is the fast path rather than the exception.
 *
 * The first cookie of this name that reads as a hint wins. Two can exist at
 * once only while a scope is changing — a host-only one written on `localhost`
 * beside a `Domain=goodbuild.cc` one, say — and a browser sends the more
 * specific first, which is the one written most recently by the app that
 * matters. Merging the roles of two cookies would be inventing a standing
 * neither of them claims.
 */
export function readSessionHint(cookieHeader: string | null | undefined): SessionHint {
  if (!cookieHeader) return NO_SESSION_HINT;

  for (const pair of cookieHeader.split(';')) {
    const separator = pair.indexOf('=');
    if (separator === -1) continue;
    if (pair.slice(0, separator).trim() !== SESSION_HINT_COOKIE) continue;

    const hint = parseHint(pair.slice(separator + 1).trim());
    if (hint.signedIn) return hint;
  }

  return NO_SESSION_HINT;
}

/**
 * Whether a request carries the hint at all.
 *
 * Kept as its own function because most callers only ever wanted this much, and
 * because it is the question the storefront asked for a year before there was
 * anything else to ask. It is `readSessionHint(...).signedIn` and must stay
 * that: two parsers for one cookie is how one of them ends up disagreeing about
 * which values are real.
 */
export function hasSessionHint(cookieHeader: string | null | undefined): boolean {
  return readSessionHint(cookieHeader).signedIn;
}
